import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../../ui/dialog";
import { Button } from "../../../ui/button";
import { Textarea } from "../../../ui/textarea";
import { Checkbox } from "../../../ui/checkbox";
import { Badge } from "../../../ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../../ui/select";
import {
  Alert,
  AlertDescription,
} from "../../../ui/alert";
import {
  Sparkles,
  Lightbulb,
  Loader2,
  Check,
  Edit2,
  Trash2,
  Mic,
  FileText,
  AlertCircle,
  RefreshCw,
  CheckSquare,
} from "lucide-react";
import { AIExtractorDialogProps, AIExtractedInsight, AIExtractedAction } from "./types";
import { cn } from "../../../../lib/utils";
import useRetryFetch from "../../../../hooks/useRetryFetch";
import { extractItems } from "../../../../api/extractAI";
import { GeneratingLoader } from "../../../ui/generating-loader";
import i18n from "i18next";

const AIExtractorDialog: React.FC<AIExtractorDialogProps> = ({
  open,
  onOpenChange,
  onExtractComplete,
  categories,
}) => {
  const { t } = useTranslation();
  const retryFetch = useRetryFetch();
  const [inputText, setInputText] = useState("");
  const [extractTypes, setExtractTypes] = useState<("insight" | "action")[]>(["insight"]);
  const [isExtracting, setIsExtracting] = useState(false);
  const [extractedInsights, setExtractedInsights] = useState<AIExtractedInsight[]>([]);
  const [extractedActions, setExtractedActions] = useState<AIExtractedAction[]>([]);
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [editText, setEditText] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [useLocalFallback, setUseLocalFallback] = useState(false);

  // Call the AI extraction API
  const handleExtract = async () => {
    if (!inputText.trim() || extractTypes.length === 0) return;

    setIsExtracting(true);
    setExtractedInsights([]);
    setExtractedActions([]);
    setError(null);

    try {
      // Try unified API first
      const response = await extractItems(retryFetch, {
        text: inputText,
        extractTypes,
        language: i18n.language,
      });

      if (response.status === "SUCCESS") {
        // Handle insights
        if (extractTypes.includes("insight") && response.insights && response.insights.length > 0) {
          const mapped: AIExtractedInsight[] = response.insights.map((insight, index) => ({
            id: `insight-${index}-${Date.now()}`,
            content: insight.content,
            confidence: insight.confidence,
            suggestedCategory: insight.suggestedCategory,
            suggestedTags: insight.suggestedTags || [],
            selected: true,
          }));
          setExtractedInsights(mapped);
        }

        // Handle action items
        if (extractTypes.includes("action") && response.actionItems && response.actionItems.length > 0) {
          const mapped: AIExtractedAction[] = response.actionItems.map((action, index) => ({
            id: `action-${index}-${Date.now()}`,
            content: action.content,
            confidence: action.confidence,
            suggestedCategory: action.suggestedCategory,
            suggestedPriority: action.suggestedPriority,
            suggestedDueDate: action.suggestedDueDate,
            dueDateLabel: action.dueDateLabel,
            selected: true,
          }));
          setExtractedActions(mapped);
        }

        // Check if any items were extracted
        const hasResults =
          (extractTypes.includes("insight") && response.insights && response.insights.length > 0) ||
          (extractTypes.includes("action") && response.actionItems && response.actionItems.length > 0);

        if (!hasResults) {
          // Try local extraction as fallback for insights only
          if (extractTypes.includes("insight")) {
            const localInsights = extractInsightsLocally(inputText);
            if (localInsights.length > 0) {
              setExtractedInsights(localInsights);
              setUseLocalFallback(true);
            } else {
              setError(t("no_items_found") || "No items could be extracted from this text. Try adding more detail or context.");
            }
          } else {
            setError(t("no_items_found") || "No action items could be extracted from this text. Try adding more detail or context.");
          }
        } else {
          setUseLocalFallback(false);
        }
      } else {
        throw new Error(response.fault || "Failed to extract items");
      }
    } catch (err) {
      console.error("AI extraction failed, using local fallback:", err);
      // Fallback to local extraction for insights only
      if (extractTypes.includes("insight")) {
        const localInsights = extractInsightsLocally(inputText);
        if (localInsights.length > 0) {
          setExtractedInsights(localInsights);
          setUseLocalFallback(true);
        } else {
          setError(t("extraction_failed") || "Failed to extract items. Please try again.");
        }
      } else {
        setError(t("extraction_failed") || "Failed to extract items. Please try again.");
      }
    } finally {
      setIsExtracting(false);
    }
  };

  // Local extraction fallback with improved algorithm
  const extractInsightsLocally = (text: string): AIExtractedInsight[] => {
    // Split into sentences
    const sentences = text
      .split(/(?<=[.!?])\s+/)
      .map((s) => s.trim())
      .filter((s) => s.length > 15);

    // Keywords that indicate insights
    const highValueKeywords = [
      "important", "key", "crucial", "essential", "critical",
      "learned", "realized", "discovered", "understood",
      "insight", "takeaway", "lesson", "principle",
      "always", "never", "should", "must",
      "best practice", "effective", "successful",
      "remember", "don't forget", "keep in mind",
    ];

    const mediumValueKeywords = [
      "better", "improve", "growth", "development",
      "strategy", "approach", "method", "technique",
      "because", "therefore", "result", "outcome",
      "helps", "enables", "allows", "leads to",
    ];

    // Score each sentence
    const scoredSentences = sentences.map((sentence) => {
      const lower = sentence.toLowerCase();
      let score = 0;

      // High value keywords
      highValueKeywords.forEach((kw) => {
        if (lower.includes(kw)) score += 3;
      });

      // Medium value keywords
      mediumValueKeywords.forEach((kw) => {
        if (lower.includes(kw)) score += 1.5;
      });

      // Quotes are often insightful
      if (sentence.includes('"') || sentence.includes("'")) score += 2;

      // Longer sentences tend to have more substance
      if (sentence.length > 80) score += 1;
      if (sentence.length > 120) score += 0.5;

      // First person reflections
      if (lower.startsWith("i ") || lower.includes(" i ")) score += 0.5;

      // Action-oriented
      if (lower.includes("need to") || lower.includes("going to") || lower.includes("will ")) {
        score += 1;
      }

      return { sentence, score };
    });

    // Filter and sort by score
    const potentialInsights = scoredSentences
      .filter((item) => item.score >= 2)
      .sort((a, b) => b.score - a.score)
      .slice(0, 6);

    // If no high-scoring sentences, take longest meaningful ones
    if (potentialInsights.length === 0) {
      const fallbackInsights = sentences
        .filter((s) => s.length > 40)
        .slice(0, 3);

      return fallbackInsights.map((content, index) => ({
        id: `extracted-${index}-${Date.now()}`,
        content: content.charAt(0).toUpperCase() + content.slice(1),
        confidence: 0.5 + Math.random() * 0.2,
        selected: true,
        suggestedTags: [],
      }));
    }

    return potentialInsights.map((item, index) => ({
      id: `extracted-${index}-${Date.now()}`,
      content: item.sentence.charAt(0).toUpperCase() + item.sentence.slice(1),
      confidence: Math.min(0.95, 0.6 + (item.score / 15)),
      selected: true,
      suggestedTags: [],
    }));
  };

  // Auto-suggest category based on content
  const suggestCategory = (content: string): string | undefined => {
    const lower = content.toLowerCase();

    // Try to match with existing categories
    for (const category of categories) {
      const categoryLower = category.name.toLowerCase();
      if (lower.includes(categoryLower)) {
        return category._id;
      }
    }

    // Common category keywords
    const categoryKeywords: Record<string, string[]> = {
      leadership: ["leader", "leadership", "team", "manage", "delegate"],
      communication: ["communicate", "listen", "speak", "feedback", "conversation"],
      growth: ["grow", "learn", "develop", "improve", "progress"],
      strategy: ["strategy", "plan", "goal", "objective", "vision"],
    };

    for (const [categoryName, keywords] of Object.entries(categoryKeywords)) {
      if (keywords.some((kw) => lower.includes(kw))) {
        const matchingCategory = categories.find(
          (c) => c.name.toLowerCase().includes(categoryName)
        );
        if (matchingCategory) return matchingCategory._id;
      }
    }

    return undefined;
  };

  const toggleInsightSelection = (id: string) => {
    setExtractedInsights((prev) =>
      prev.map((insight) =>
        insight.id === id ? { ...insight, selected: !insight.selected } : insight
      )
    );
  };

  const startEditing = (index: number) => {
    setEditingIndex(index);
    setEditText(extractedInsights[index].content);
  };

  const saveEdit = () => {
    if (editingIndex === null) return;
    setExtractedInsights((prev) =>
      prev.map((insight, idx) =>
        idx === editingIndex ? { ...insight, content: editText } : insight
      )
    );
    setEditingIndex(null);
    setEditText("");
  };

  const removeInsight = (id: string) => {
    setExtractedInsights((prev) => prev.filter((insight) => insight.id !== id));
  };

  const updateInsightCategory = (id: string, categoryId: string) => {
    setExtractedInsights((prev) =>
      prev.map((insight) =>
        insight.id === id
          ? { ...insight, suggestedCategory: categoryId === "none" ? undefined : categoryId }
          : insight
      )
    );
  };

  // Action item handlers
  const toggleActionSelection = (id: string) => {
    setExtractedActions((prev) =>
      prev.map((action) =>
        action.id === id ? { ...action, selected: !action.selected } : action
      )
    );
  };

  const removeAction = (id: string) => {
    setExtractedActions((prev) => prev.filter((action) => action.id !== id));
  };

  const updateActionCategory = (id: string, categoryId: string) => {
    setExtractedActions((prev) =>
      prev.map((action) =>
        action.id === id
          ? { ...action, suggestedCategory: categoryId === "none" ? undefined : categoryId }
          : action
      )
    );
  };

  const handleAddSelected = () => {
    const selectedInsights = extractedInsights.filter((i) => i.selected);
    const selectedActions = extractedActions.filter((a) => a.selected);
    onExtractComplete(selectedInsights, selectedActions);
    handleClose();
  };

  const handleClose = () => {
    setInputText("");
    setExtractTypes(["insight"]);
    setExtractedInsights([]);
    setExtractedActions([]);
    setIsExtracting(false);
    setEditingIndex(null);
    setError(null);
    onOpenChange(false);
  };

  const handleRetry = () => {
    setError(null);
    setExtractedInsights([]);
    setExtractedActions([]);
  };

  const selectAll = () => {
    setExtractedInsights((prev) =>
      prev.map((insight) => ({ ...insight, selected: true }))
    );
    setExtractedActions((prev) =>
      prev.map((action) => ({ ...action, selected: true }))
    );
  };

  const deselectAll = () => {
    setExtractedInsights((prev) =>
      prev.map((insight) => ({ ...insight, selected: false }))
    );
    setExtractedActions((prev) =>
      prev.map((action) => ({ ...action, selected: false }))
    );
  };

  const toggleExtractType = (type: "insight" | "action") => {
    setExtractTypes((prev) => {
      if (prev.includes(type)) {
        // Don't allow unchecking if it's the only one selected
        if (prev.length === 1) return prev;
        return prev.filter((t) => t !== type);
      } else {
        return [...prev, type];
      }
    });
  };

  const selectedCount =
    extractedInsights.filter((i) => i.selected).length +
    extractedActions.filter((a) => a.selected).length;

  const totalExtracted = extractedInsights.length + extractedActions.length;

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[650px] max-h-[85vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-violet-100 to-purple-100 flex items-center justify-center">
              <Sparkles className="h-4 w-4 text-violet-600" />
            </div>
            {t("ai_insight_extractor") || "AI Insight Extractor"}
          </DialogTitle>
          <DialogDescription>
            {t("ai_extractor_description") ||
              "Paste your notes, meeting summaries, or thoughts below and let AI identify key insights for you."}
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto py-4 space-y-4">
          {/* Error State */}
          {error && (
            <Alert variant="destructive" className="bg-red-50 border-red-200">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription className="flex items-center justify-between">
                <span>{error}</span>
                <Button variant="ghost" size="sm" onClick={handleRetry} className="ml-2">
                  <RefreshCw className="h-3 w-3 mr-1" />
                  {t("try_again") || "Try Again"}
                </Button>
              </AlertDescription>
            </Alert>
          )}

          {/* Input Section */}
          {totalExtracted === 0 && !error && (
            <div className="space-y-3">
              {/* Extraction Type Selection */}
              <div className="flex gap-2 mb-3">
                <Button
                  variant={extractTypes.includes("insight") ? "default" : "outline"}
                  size="sm"
                  onClick={() => toggleExtractType("insight")}
                  className={cn(
                    "flex-1",
                    extractTypes.includes("insight") && "bg-amber-500 hover:bg-amber-600"
                  )}
                >
                  <Lightbulb className="h-3.5 w-3.5 mr-1.5" />
                  {t("insights") || "Insights"}
                </Button>
                <Button
                  variant={extractTypes.includes("action") ? "default" : "outline"}
                  size="sm"
                  onClick={() => toggleExtractType("action")}
                  className={cn(
                    "flex-1",
                    extractTypes.includes("action") && "bg-primary hover:bg-primary"
                  )}
                >
                  <CheckSquare className="h-3.5 w-3.5 mr-1.5" />
                  {t("action_items") || "Action Items"}
                </Button>
              </div>
              <Textarea
                value={inputText}
                onChange={(e) => setInputText(e.target.value)}
                placeholder={
                  t("paste_your_notes_placeholder") ||
                  `Paste your notes, meeting summary, or thoughts here...

Example:
Had a great conversation with Sarah today about leadership. She mentioned that the best leaders ask questions instead of giving answers. Also talked about how vulnerability builds trust and creates psychological safety in teams. I realized that I need to practice active listening more - it's not just about hearing words but understanding the emotions behind them.`
                }
                className="min-h-[220px] resize-none text-sm"
                disabled={isExtracting}
              />

              {/* Character count & tips */}
              <div className="flex items-center justify-between text-xs text-muted-foreground">
                <div className="flex items-center gap-3">
                  <Button variant="ghost" size="sm" className="h-7 text-xs" disabled>
                    <Mic className="h-3 w-3 mr-1" />
                    {t("voice_input") || "Voice"} ({t("coming_soon") || "Soon"})
                  </Button>
                  <Button variant="ghost" size="sm" className="h-7 text-xs" disabled>
                    <FileText className="h-3 w-3 mr-1" />
                    {t("upload_file") || "Upload"} ({t("coming_soon") || "Soon"})
                  </Button>
                </div>
                <span className={cn(
                  inputText.length > 50 ? "text-green-600" : "text-gray-400"
                )}>
                  {inputText.length} {t("characters") || "characters"}
                </span>
              </div>

              {/* Tips */}
              {inputText.length > 0 && inputText.length < 100 && (
                <p className="text-xs text-amber-600 bg-amber-50 p-2 rounded">
                  💡 {t("extraction_tip") || "Tip: Add more context for better results. Include your reflections, learnings, or action points."}
                </p>
              )}
            </div>
          )}

          {/* Extraction Animation */}
          {isExtracting && (
            <div className="flex items-center justify-center py-16">
              <GeneratingLoader context="extraction" />
            </div>
          )}

          {/* Extracted Items */}
          {totalExtracted > 0 && !isExtracting && (
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <p className="text-sm font-medium">
                    {t("ai_extracted_items") || "AI-Extracted Items"}
                  </p>
                  {useLocalFallback && (
                    <Badge variant="outline" className="text-xs text-amber-600 border-amber-200">
                      {t("local_extraction") || "Smart Extract"}
                    </Badge>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={selectAll}
                    className="h-6 text-xs"
                  >
                    {t("select_all") || "Select All"}
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={deselectAll}
                    className="h-6 text-xs"
                  >
                    {t("deselect_all") || "Deselect All"}
                  </Button>
                  <Badge variant="secondary" className="text-xs">
                    {totalExtracted} {t("found") || "found"}
                  </Badge>
                </div>
              </div>

              <div className="space-y-2 max-h-[300px] overflow-y-auto pr-1">
                {extractedInsights.map((insight, index) => (
                  <div
                    key={insight.id}
                    className={cn(
                      "relative p-3 rounded-lg border transition-all",
                      insight.selected
                        ? "bg-amber-50/50 border-amber-200 shadow-sm"
                        : "bg-gray-50/50 border-gray-200 opacity-60"
                    )}
                  >
                    {/* Editing mode */}
                    {editingIndex === index ? (
                      <div className="space-y-2">
                        <Textarea
                          value={editText}
                          onChange={(e) => setEditText(e.target.value)}
                          className="min-h-[80px] text-sm"
                          autoFocus
                        />
                        <div className="flex justify-end gap-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setEditingIndex(null)}
                          >
                            {t("cancel")}
                          </Button>
                          <Button size="sm" onClick={saveEdit}>
                            {t("save")}
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <>
                        {/* Selection checkbox & content */}
                        <div className="flex items-start gap-3">
                          <Checkbox
                            checked={insight.selected}
                            onCheckedChange={() => toggleInsightSelection(insight.id)}
                            className="mt-0.5 data-[state=checked]:bg-amber-500 data-[state=checked]:border-amber-500"
                          />
                          <div className="flex-1 min-w-0">
                            <div className="flex items-start gap-2">
                              <Lightbulb className="h-4 w-4 text-amber-500 flex-shrink-0 mt-0.5" />
                              <p className="text-sm text-gray-700 leading-relaxed">
                                {insight.content}
                              </p>
                            </div>

                            {/* Category selector & confidence */}
                            <div className="mt-2 flex items-center gap-2 flex-wrap">
                              <Select
                                value={insight.suggestedCategory || "none"}
                                onValueChange={(val) => updateInsightCategory(insight.id, val)}
                              >
                                <SelectTrigger className="h-7 w-[140px] text-xs">
                                  <SelectValue placeholder={t("category")} />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="none">
                                    {t("no_category") || "No Category"}
                                  </SelectItem>
                                  {categories.map((cat) => (
                                    <SelectItem key={cat._id} value={cat._id}>
                                      <div className="flex items-center gap-1.5">
                                        <div
                                          className="w-2 h-2 rounded-full"
                                          style={{ backgroundColor: cat.color }}
                                        />
                                        {cat.name}
                                      </div>
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>

                              {/* Confidence badge */}
                              <Badge
                                variant="outline"
                                className={cn(
                                  "text-xs",
                                  insight.confidence >= 0.8
                                    ? "border-green-200 text-green-700 bg-green-50"
                                    : insight.confidence >= 0.6
                                    ? "border-amber-200 text-amber-700 bg-amber-50"
                                    : "border-gray-200 text-gray-600 bg-gray-50"
                                )}
                              >
                                {Math.round(insight.confidence * 100)}%{" "}
                                {t("match") || "match"}
                              </Badge>
                            </div>
                          </div>

                          {/* Action buttons */}
                          <div className="flex items-center gap-1">
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-7 w-7 p-0 hover:bg-gray-100"
                              onClick={() => startEditing(index)}
                            >
                              <Edit2 className="h-3.5 w-3.5" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-7 w-7 p-0 text-destructive hover:text-destructive hover:bg-red-50"
                              onClick={() => removeInsight(insight.id)}
                            >
                              <Trash2 className="h-3.5 w-3.5" />
                            </Button>
                          </div>
                        </div>
                      </>
                    )}
                  </div>
                ))}

                {/* Action Items */}
                {extractedActions.map((action) => (
                  <div
                    key={action.id}
                    className={cn(
                      "relative p-3 rounded-lg border transition-all",
                      action.selected
                        ? "bg-primary/5/50 border-primary/20 shadow-sm"
                        : "bg-gray-50/50 border-gray-200 opacity-60"
                    )}
                  >
                    <div className="flex items-start gap-3">
                      <Checkbox
                        checked={action.selected}
                        onCheckedChange={() => toggleActionSelection(action.id)}
                        className="mt-0.5 data-[state=checked]:bg-primary data-[state=checked]:border-primary"
                      />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start gap-2">
                          <CheckSquare className="h-4 w-4 text-primary flex-shrink-0 mt-0.5" />
                          <p className="text-sm text-gray-700 leading-relaxed">
                            {action.content}
                          </p>
                        </div>

                        {/* Category, Priority & Due Date */}
                        <div className="mt-2 flex items-center gap-2 flex-wrap">
                          <Select
                            value={action.suggestedCategory || "none"}
                            onValueChange={(val) => updateActionCategory(action.id, val)}
                          >
                            <SelectTrigger className="h-7 w-[140px] text-xs">
                              <SelectValue placeholder={t("category")} />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="none">
                                {t("no_category") || "No Category"}
                              </SelectItem>
                              {categories.map((cat) => (
                                <SelectItem key={cat._id} value={cat._id}>
                                  <div className="flex items-center gap-1.5">
                                    <div
                                      className="w-2 h-2 rounded-full"
                                      style={{ backgroundColor: cat.color }}
                                    />
                                    {cat.name}
                                  </div>
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>

                          {/* Priority Badge */}
                          {action.suggestedPriority && (
                            <Badge
                              variant="outline"
                              className={cn(
                                "text-xs",
                                action.suggestedPriority === "high"
                                  ? "border-red-200 text-red-700 bg-red-50"
                                  : action.suggestedPriority === "medium"
                                  ? "border-amber-200 text-amber-700 bg-amber-50"
                                  : "border-gray-200 text-gray-600 bg-gray-50"
                              )}
                            >
                              {action.suggestedPriority.toUpperCase()}
                            </Badge>
                          )}

                          {/* Due Date Label */}
                          {action.dueDateLabel && (
                            <Badge variant="outline" className="text-xs border-primary/20 text-primary bg-primary/5">
                              {action.dueDateLabel}
                            </Badge>
                          )}

                          {/* Confidence badge */}
                          <Badge
                            variant="outline"
                            className={cn(
                              "text-xs",
                              action.confidence >= 0.8
                                ? "border-green-200 text-green-700 bg-green-50"
                                : action.confidence >= 0.6
                                ? "border-amber-200 text-amber-700 bg-amber-50"
                                : "border-gray-200 text-gray-600 bg-gray-50"
                            )}
                          >
                            {Math.round(action.confidence * 100)}%{" "}
                            {t("match") || "match"}
                          </Badge>
                        </div>
                      </div>

                      {/* Action buttons */}
                      <div className="flex items-center gap-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-7 w-7 p-0 text-destructive hover:text-destructive hover:bg-red-50"
                          onClick={() => removeAction(action.id)}
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Re-extract option */}
              <div className="pt-2 flex items-center justify-between">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    setExtractedInsights([]);
                    setExtractedActions([]);
                  }}
                  className="text-xs text-muted-foreground"
                >
                  {t("try_different_text") || "← Try different text"}
                </Button>
                <p className="text-xs text-muted-foreground">
                  {t("edit_before_adding") || "You can edit items before adding them"}
                </p>
              </div>
            </div>
          )}
        </div>

        <DialogFooter className="gap-2 sm:gap-0 border-t pt-4">
          <Button variant="outline" onClick={handleClose}>
            {t("cancel")}
          </Button>

          {totalExtracted === 0 ? (
            <Button
              onClick={handleExtract}
              disabled={!inputText.trim() || inputText.length < 20 || isExtracting || extractTypes.length === 0}
              className="bg-gradient-to-r from-violet-500 to-purple-500 hover:from-violet-600 hover:to-purple-600"
            >
              {isExtracting ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  {t("extracting") || "Extracting..."}
                </>
              ) : (
                <>
                  <Sparkles className="h-4 w-4 mr-2" />
                  {t("extract_items") || "Extract Items"}
                </>
              )}
            </Button>
          ) : (
            <Button
              onClick={handleAddSelected}
              disabled={selectedCount === 0}
              className="bg-amber-500 hover:bg-amber-600"
            >
              <Check className="h-4 w-4 mr-2" />
              {t("add_selected") || "Add Selected"} ({selectedCount})
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default AIExtractorDialog;
